import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";

import { ROUTE_PATHS } from "../utils/routes";
import { OfferApi, SubmissionApi } from "../utils/api";
import { handleSubmissionError, formatPhoneNumber } from "../utils/FormUtils";
import { isEmpty } from "../utils/Helpers";
import AnalyticsService from "../utils/AnalyticsService";
import { GA4_EVENTS } from "../utils/ga4Events";
import { AppContext } from "../components/context/AppContext";
import {
  WelcomeComponent,
  GetStartedComponent,
  StatusLookupComponent,
  QuestionsAndContactComponent,
} from "../components";
import { LegalText, ErrorMessage } from "../components/styled";
import { EXTERNAL_ROUTE_PATHS } from "../utils/externalRoutes";

export const Home = ({ location, history, match }) => {
  const { state, dispatch } = useContext(AppContext);
  const [isRecaptchaVisible, setIsRecaptchaVisible] = useState(false);
  const [isLoadingCaptcha, setIsLoadingCaptcha] = useState(false);
  const [isLoggingIn] = useState(false);            // setter removed (unused)
  const [isNetworkOutage, setIsNetworkOutage] = useState(false);
  const [errorMessage] = useState();                // setter removed (unused)
  const [errorMessageLookup, setErrorMessageLookup] = useState();

  // Stable, simple values derived from state/location for effect deps
  const lookupValuesFromState =
    (state && state.lookupValues) ? state.lookupValues : {};
  const promoCodeFromState = lookupValuesFromState.promoCode;

  // Read promo from URL (?code=...) whenever the query string changes
  const promoFromUrl = React.useMemo(function () {
    const params = new URLSearchParams(location.search);
    return (params.get("code") || "").trim();
  }, [location.search]);

  // If URL contains ?code=..., store it in state.lookupValues.promoCode and then
  // clean the URL (remove ?code=...) without reloading.
  useEffect(function () {
    if (!promoFromUrl) return;

    const currentLookup = lookupValuesFromState;

    if (currentLookup.promoCode !== promoFromUrl) {
      dispatch({
        type: "SETLOOKUPVALUES",
        payload: { ...currentLookup, promoCode: promoFromUrl },
      });
    }

    const params = new URLSearchParams(location.search);
    params.delete("code");
    const newSearch = params.toString();

    if ((newSearch ? `?${newSearch}` : "") !== location.search) {
      history.replace({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : "",
        hash: location.hash,
        state: location.state,
      });
    }
  }, [
    promoFromUrl,
    promoCodeFromState,
    lookupValuesFromState,  // ✅ avoids depending on entire `state`
    dispatch,
    history,
    location,               // ✅ no complex member expressions here
  ]);

  const cleanAndOrderSubmissions = (unorderedSubmissions) => {
    const mergedSubmissions = [
      ...unorderedSubmissions.submissions,
      ...unorderedSubmissions.iSubmissions,
    ];
    const distinctSubmissions = [];
    const map = new Map();

    for (var i = 0; i < mergedSubmissions.length; i++) {
      const submission = mergedSubmissions[i];
      if (!map.has(submission.trackingId)) {
        map.set(submission.trackingId, true);
        distinctSubmissions.push(submission);
      }
    }

    return distinctSubmissions.sort(function (sub1, sub2) {
      if (sub1.stampDate > sub2.stampDate) return -1;
      if (sub1.stampDate < sub2.stampDate) return 1;
      return 0;
    });
  };

  // ─── GA4: Track landing page view ───────────────────────────────────
  useEffect(function () {
    AnalyticsService.sendGA4FunnelEvent(GA4_EVENTS.LANDING_PAGE_VIEWED, {
      page_path: ROUTE_PATHS.HOME,
    });
  }, []);

  // Recaptcha/maintenance check
  useEffect(function () {
    var cancelled = false;

    async function getRecaptchaVisible() {
      setIsLoadingCaptcha(true);
      try {
        const maintenance = await OfferApi.getAssetsByKeywordHookup("MaintenanceFlag");
        if (cancelled) return;

        if (
          maintenance &&
          maintenance.notes &&
          String(maintenance.notes).toLowerCase() === "true"
        ) {
          setIsNetworkOutage(true);
          return;
        }

        const result = await OfferApi.getClientReCaptchaVisible();
        if (cancelled) return;

        setIsRecaptchaVisible(!!result);
        setIsLoadingCaptcha(false);
      } catch (error) {
        if (cancelled) return;
        setIsNetworkOutage(true);
        setIsLoadingCaptcha(false);
      }
    }

    getRecaptchaVisible();
    return function cleanup() {
      cancelled = true;
    };
  }, []);

  const onStatusLookupSubmit = async (values, { setSubmitting, setStatus }) => {
    values.phoneNumber = formatPhoneNumber(values.phoneNumber);
    const lookupValues = { ...values };
    dispatch({ type: "SETLOOKUPVALUES", payload: lookupValues });
    setErrorMessageLookup("");

    try {
      values.phoneNumber = formatPhoneNumber(values.phoneNumber);
      const response = await SubmissionApi.orderStatusLookup(values);

      if (isEmpty(response.submissions) && isEmpty(response.iSubmissions)) {
        setErrorMessageLookup("There are no submissions based on your search criteria");
        setSubmitting(false);
      } else {
        const submissions = cleanAndOrderSubmissions(response);
        dispatch({ type: "SETSUBMISSIONS", payload: submissions });
        AnalyticsService.sendGa4Event({
          event: "hookup_lookup",
          category: "Home",
          action: "OSL",
          label: "Upload Site",
        });
        // GA4 funnel: status lookup submitted
        AnalyticsService.sendGA4FunnelEvent(GA4_EVENTS.STATUS_LOOKUP_SUBMITTED);
        history.push(ROUTE_PATHS.TRACKER);
      }
    } catch (error) {
      handleSubmissionError(setSubmitting, setStatus);
    }
  };

  return (
    <>
      <WelcomeComponent />
      {isNetworkOutage ? (
        <div className="text-center container">
          <h1 className="magenta">Temporarily Unavailable</h1>
          <p>This site is currently under going scheduled maintenance. We&apos;ll be back soon.</p>
          <p>Thank you for your patience.</p>
        </div>
      ) : (
        <>
          {!isLoadingCaptcha && <GetStartedComponent isBroadband={false} />}
          {!isLoggingIn && errorMessage && (
            <div className="container">
              <div className="row no-gutters2">
                <div className="mx-auto col-md-12">
                  <ErrorMessage className="text-center">{errorMessage}</ErrorMessage>
                </div>
              </div>
            </div>
          )}

          <div className="container">
            <div className="row no-gutters2">
              <div className="mx-auto col-md-12">
                <QuestionsAndContactComponent isHomePage />
              </div>
              <div className="mx-auto col-md-12">
                <LegalText>
                  Capable device required; coverage not available in some areas. Some uses may require
                  certain plan or feature; see plan for details. While 5G access won&apos;t require a
                  certain plan or feature, some uses/services might. See{" "}
                  <a href={EXTERNAL_ROUTE_PATHS.COVERAGE_MAP}>Coverage details</a>,{" "}
                  <a href={EXTERNAL_ROUTE_PATHS.TMO_LEGAL}>Terms and Conditions</a>, and{" "}
                  <a href={EXTERNAL_ROUTE_PATHS.INTERNET_POLICY}>Open Internet</a> information for
                  network management details (like video optimization).
                </LegalText>
              </div>
            </div>
            <p className="hookupLookup">
              To check code usage or re-send a code e-mail (if applicable),{" "}
              <Link to={ROUTE_PATHS.LOOKUP}>click here.</Link>
            </p>
          </div>

          <StatusLookupComponent
            onSubmit={onStatusLookupSubmit}
            isReCaptchaVisible={isRecaptchaVisible}
            error={errorMessageLookup}
          />
        </>
      )}
    </>
  );
};

Home.propTypes = {
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};
